import React, {ChangeEvent, useState} from "react";
import {useTranslation} from "react-i18next";
import {Dialog as MuiDialog, DialogActions, Stack, Typography, Avatar} from "@mui/material";
import {Box, Button, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup} from "@mui/material";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import ArrowForward from "@mui/icons-material/ArrowForward";
import MapIcon from "@mui/icons-material/Map";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import {BoundaryTypeOption} from "../../types/boundaryTypeOption";
import {ActionButton} from "../base/ActionButton";

export interface WizardWelcomeDialogProps {
    open: boolean;
    handleClick: (boundaryType: BoundaryTypeOption) => void;
    onClose: (isSuccessful: boolean) => void;
}

export const WizardWelcomeDialog: React.FC<WizardWelcomeDialogProps> = (props) => {
    const {t} = useTranslation();
    const [boundaryType, setBoundaryType] = useState(BoundaryTypeOption.MANUAL);
    const [open, setOpen] = useState(props.open);

    const handleBoundaryTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
        setBoundaryType(event.target.value as BoundaryTypeOption);
    };
    return (
        <MuiDialog
            PaperProps={{
                sx: {
                    borderRadius: "6px",
                    minWidth: "900px",
                    width: "900px",
                },
            }}
            sx={{
                backdropFilter: "blur(5px) sepia(5%)",
            }}
            onClose={() => {
                props.onClose(false);
            }}
            open={open}
            disableRestoreFocus
        >
            <DialogTitle
                sx={{
                    height: "24px",
                    fontSize: "16px",
                    lineHeight: "normal",
                    textTransform: "uppercase",
                    verticalAlign: "middle",
                    fontWeight: "500",
                    bgcolor: "#F3F5FA",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                    }}
                >
                    {<FmdGoodOutlinedIcon color="primary" />}
                    <Box sx={{ml: "0.75rem"}}>{t("dialogs.wizard_welcome.title")}</Box>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{padding: 0}}>
                <Box padding="1.5rem">
                    <Stack direction="column" alignItems="center" gap="3rem" paddingY="2rem">
                        <Typography
                            sx={{
                                fontSize: "1.5rem",
                                fontWeight: "520",
                            }}
                        >
                            {t("dialogs.wizard_welcome.add_label")}
                        </Typography>

                        <Stack direction="column" alignItems="center" gap="2rem">
                            <Stack direction="row" gap="1.5rem">
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        bgcolor: "#F3F5FA",
                                        width: "4.2rem",
                                        height: "4.2rem",
                                    }}
                                >
                                    <MapIcon color="primary" sx={{fontSize: 40}} />
                                </Avatar>
                                <Stack direction="column" alignItems="left" maxWidth="25rem">
                                    <Typography
                                        fontSize="1rem"
                                        fontWeight={520}
                                        line-height={"1.5rem"}
                                        textTransform="uppercase"
                                        marginBottom="0.25rem"
                                    >
                                        {t("dialogs.wizard_welcome.step1_title")}
                                    </Typography>
                                    <Typography fontSize="0.875rem" marginBottom="0.4rem">
                                        {t("dialogs.wizard_welcome.step1_description")}
                                    </Typography>
                                    <RadioGroup value={boundaryType} onChange={handleBoundaryTypeChange}>
                                        <FormControlLabel
                                            value={BoundaryTypeOption.MANUAL}
                                            control={<Radio sx={{paddingY: "0.4rem"}} />}
                                            label={t("dialogs.wizard_welcome.boundary_type_manual")}
                                        />
                                        <FormControlLabel
                                            value={BoundaryTypeOption.UPLOAD_FILE}
                                            control={<Radio sx={{paddingY: "0.4rem"}} />}
                                            label={t("dialogs.wizard_welcome.boundary_type_upload")}
                                        />
                                    </RadioGroup>
                                </Stack>
                            </Stack>

                            <Stack direction="row" gap="1.5rem">
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        bgcolor: "#F3F5FA",
                                        width: "4.2rem",
                                        height: "4.2rem",
                                    }}
                                >
                                    <PlaylistAddIcon color="primary" sx={{fontSize: 45}} />
                                </Avatar>
                                <Stack direction="column" alignItems="left" maxWidth="25rem">
                                    <Typography
                                        fontSize="1rem"
                                        fontWeight={520}
                                        line-height={"1.5rem"}
                                        textTransform="uppercase"
                                    >
                                        {t("dialogs.wizard_welcome.step2_title")}
                                    </Typography>
                                    <Typography fontSize="0.875rem">
                                        {t("dialogs.wizard_welcome.step2_description")}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Stack>

                        <Button
                            startIcon={<ArrowForward />}
                            variant="contained"
                            onClick={() => {
                                setOpen(false);
                                props.handleClick(boundaryType);
                            }}
                        >
                            {t("dialogs.wizard_welcome.start_button")}
                        </Button>
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions
                sx={{
                    height: "2rem",
                    fontSize: "16px",
                    textTransform: "uppercase",
                    verticalAlign: "middle",
                    fontWeight: "500",
                    bgcolor: "#F3F5FA",
                    paddingX: "1.5rem",
                    paddingY: "1.125rem",
                    justifyContent: "end",
                }}
            >
                <ActionButton
                    id="cancel"
                    icon="back"
                    color={"secondary"}
                    onClick={() => {
                        props.onClose(false);
                        setOpen(false);
                    }}
                    sx={{ml: "1rem"}}
                >
                    {t("dialogs.actions.cancel")}
                </ActionButton>
            </DialogActions>
        </MuiDialog>
    );
};
